import AdvancedSearch from '@app/components/AdvancedSearch'
import BidirectionalSort from '@app/components/BidirectionalSort'
import CheckBox from '@app/components/CheckBox'
import DateRangeSelector from '@app/components/DateRangeSelector'
import FeedItemEditor from '@app/components/FeedItemEditor'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import PropertyTypeSelector from '@app/components/PropertyTypeSelector'
import SearchBar from '@app/components/SearchBar'
import { FeedItemType, LeadPriority, PropertyType } from '@app/models/enums'
import { IPropertyLead } from '@app/models/PropertyLead'
import { ICurrentUserState } from '@app/store/modules/account'
import { IGlobalConfigurationState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatDate, humanize } from '@app/utils/formatting'
import { IconName } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { List } from 'immutable'
import * as qs from 'query-string'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { RouteComponentProps } from 'react-router-dom'
import { CellMeasurer, CellMeasurerCache, Column } from 'react-virtualized'
import {
  Alert,
  Button,
  Col,
  Collapse,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap'
import {
  actionCreators,
  ILeadsListState,
  LeadsScope,
} from '../modules/leadsList'

type ComponentProps = ILeadsListState &
  ICurrentUserState &
  IGlobalConfigurationState &
  typeof actionCreators &
  RouteComponentProps<{}>

interface ILeadsListPageState {
  showQuickAdd: boolean
}

export default class LeadsListPage extends React.PureComponent<
  ComponentProps,
  ILeadsListPageState
> {
  private tableRef: React.RefObject<InfiniteScrollTable>
  private loadNewIntervalId?: number

  private cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 50,
  })

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()

    this.state = {
      showQuickAdd: false,
    }
  }

  public componentDidMount() {
    if (this.props.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }

    this.loadNewIntervalId = window.setInterval(this._loadNewFeedItems, 15000)
  }

  public componentDidUpdate(prevProps: ComponentProps) {
    if (this.props.location.search !== prevProps.location.search) {
      const query = qs.parse(this.props.location.search)
      if (query.filter) {
        this._onChangeFilter(query.filter)
      }
    }

    if (this.props.itemChangedIndex !== undefined) {
      this._onItemLayoutChanged(this.props.itemChangedIndex)
      this.props.resetItemChangedIndex()
    }
  }

  public componentWillUnmount() {
    if (this.loadNewIntervalId) {
      window.clearInterval(this.loadNewIntervalId)
      this.loadNewIntervalId = undefined
    }
  }

  public render() {
    this.cache.clearAll()

    return (
      <DocumentTitle title='Leads'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <div className='d-flex align-items-center justify-content-between'>
              <div>
                <h3>Leads</h3>
              </div>
              <div>
                <Button color='link' onClick={this._toggleShowQuickAdd}>
                  {this.state.showQuickAdd
                    ? 'Hide quick add'
                    : 'Show quick add'}
                </Button>
              </div>
            </div>
            <Collapse isOpen={this.state.showQuickAdd}>
              <div className='mb-3'>
                <FeedItemEditor itemType={FeedItemType.Lead} />
              </div>
            </Collapse>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for something?'
              size='sm'
              value={this.props.filterText}
            />
            <AdvancedSearch className='mt-1' expand='md'>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Date</Label>
                  <DateRangeSelector
                    bsSize='sm'
                    value={this.props.dueDate}
                    onChange={this._onChangeDueDate}
                  />
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.status}
                    onChange={this._onChangeStatus}
                  >
                    {this.props.configuration.assignmentStatuses
                      .map((c, i) => (
                        <option key={`status-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Sort By</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.sortOrder}
                    onChange={this._onChangeSort}
                  >
                    {this.props.configuration.leadSortOptions
                      .map((c, i) => (
                        <option key={`sort-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <BidirectionalSort
                md={4}
                xl={3}
                orderBy={this.props.orderBy}
                changeOrdering={this._onChangeOrderBy}
              />
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Priority</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.priority}
                    onChange={this._onChangePriority}
                  >
                    <option key={`priority-all`} value=''>
                      All
                    </option>
                    {this.props.configuration.leadPriorityOptions
                      .map((c, i) => (
                        <option key={`priority-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col
                md={4}
                xl={4}
                style={{ alignSelf: 'flex-end', marginBottom: '4px' }}
              >
                <FormGroup>
                  <Label />
                  <CheckBox
                    value={this.props.includeArchived}
                    onChange={this._onChangeIncludeArchived}
                  />{' '}
                  Include archived
                </FormGroup>
              </Col>
              <Col md={8} xl={6}>
                <FormGroup>
                  <Label>Property Type</Label>
                  <PropertyTypeSelector
                    value={this.props.propertyTypes}
                    onChange={this._onChangePropertyTypes}
                  />
                </FormGroup>
              </Col>
            </AdvancedSearch>
            <div className='mb-3 mt-5'>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    active={this.props.scope === LeadsScope.Mine}
                    onClick={() => this._onChangeScope(LeadsScope.Mine)}
                  >
                    {LeadsScope.Mine}
                  </NavLink>
                </NavItem>
                {this.props.user && this.props.user.permissions.isAdmin && (
                  <NavItem>
                    <NavLink
                      active={this.props.scope === LeadsScope.All}
                      onClick={() => this._onChangeScope(LeadsScope.All)}
                    >
                      {LeadsScope.All}
                    </NavLink>
                  </NavItem>
                )}
              </Nav>
            </div>
          </div>
          {this._renderNewItemsNotification()}
          <div>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={this.cache.rowHeight}
              loadItems={this.props.loadLeads}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              overscanRowCount={3}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellRenderer={({ rowData }) => {
                  const propLead = rowData as IPropertyLead

                  let priorityIconName: IconName
                  let iconClasses: string | undefined
                  priorityIconName = 'minus-circle'

                  switch (propLead.lead.leadPriority) {
                    case LeadPriority.High:
                      iconClasses = 'text-danger'
                      priorityIconName = 'exclamation-circle'
                      break

                    case LeadPriority.Low:
                      iconClasses = 'text-info'
                      priorityIconName = 'arrow-alt-circle-down'
                      break

                    default:
                      priorityIconName = 'minus-circle'
                      break
                  }

                  return (
                    <div
                      className={`${iconClasses}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        this.toggleLeadPriority(
                          propLead.lead.id,
                          propLead.lead.leadPriority,
                        )
                      }}
                    >
                      <FontAwesomeIcon icon={['fal', priorityIconName]} />
                    </div>
                  )
                }}
                dataKey='markup'
                style={{ whiteSpace: 'normal' }}
                width={20}
              />
              <Column
                cellRenderer={({ dataKey, parent, rowIndex, rowData }) => {
                  const datum = this.props.items.get(
                    rowIndex % this.props.items.size,
                  )
                  const propLead = rowData as IPropertyLead

                  return (
                    <CellMeasurer
                      columnIndex={0}
                      cache={this.cache}
                      key={dataKey}
                      parent={parent}
                      rowIndex={rowIndex}
                    >
                      <div className='py-2'>
                        <div style={{ fontWeight: 'bold' }}>
                          {propLead.lead.title}
                        </div>
                      </div>
                    </CellMeasurer>
                  )
                }}
                dataKey='markup'
                label='Title'
                style={{ whiteSpace: 'normal' }}
                flexGrow={1}
                width={50}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const propLead = rowData as IPropertyLead

                  return humanize(propLead.lead.leadType)
                }}
                dataKey='leadType'
                label='Type'
                headerClassName='d-none d-lg-block'
                className='d-none d-lg-block'
                style={{ whiteSpace: 'normal' }}
                width={150}
              />
              <Column
                cellRenderer={({ dataKey, parent, rowIndex, rowData }) => {
                  const propLead = rowData as IPropertyLead

                  if (propLead.properties.size === 1) {
                    return (
                      <CellMeasurer
                        columnIndex={0}
                        cache={this.cache}
                        key={dataKey}
                        parent={parent}
                        rowIndex={rowIndex}
                      >
                        <div className='overflow-text'>
                          {propLead.properties.first().name}
                        </div>
                      </CellMeasurer>
                    )
                  }

                  if (propLead.properties.size === 2) {
                    return (
                      <CellMeasurer
                        columnIndex={0}
                        cache={this.cache}
                        key={dataKey}
                        parent={parent}
                        rowIndex={rowIndex}
                      >
                        <div className='overflow-text'>
                          {`${propLead.properties.first().name} and ${
                            propLead.properties.skip(1).first().name
                          }`}
                        </div>
                      </CellMeasurer>
                    )
                  }

                  if (propLead.properties.size > 2) {
                    return (
                      <CellMeasurer
                        columnIndex={0}
                        cache={this.cache}
                        key={dataKey}
                        parent={parent}
                        rowIndex={rowIndex}
                      >
                        <div className='overflow-text'>
                          {`${propLead.properties.first().name}, ${
                            propLead.properties.skip(1).first().name
                          } and ${propLead.properties.size - 2} more...`}
                        </div>
                      </CellMeasurer>
                    )
                  }

                  return 'No Property'
                }}
                dataKey='property'
                label='Property'
                style={{ whiteSpace: 'normal' }}
                flexGrow={1}
                width={50}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const propLead = rowData as IPropertyLead

                  return formatDate(propLead.lead.dateCreated, 'MM/DD/YYYY')
                }}
                dataKey='date'
                label='Date'
                width={90}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const propLead = rowData as IPropertyLead

                  if (!this.props.user) {
                    return
                  }

                  if (propLead.lead.isArchived) {
                    return
                  }

                  if (
                    propLead.lead.author.contactId === this.props.user.id ||
                    this.props.user.permissions.isAdmin ||
                    propLead.lead.owners.contains(this.props.user.id)
                  ) {
                    return (
                      <a
                        title='Archive'
                        className='text-primary'
                        onClick={(e) => {
                          e.stopPropagation()
                          this.props.archiveLead(propLead.lead.id)
                        }}
                      >
                        <FontAwesomeIcon icon={['fas', 'archive']} />
                      </a>
                    )
                  }
                }}
                dataKey='archive'
                width={20}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private toggleLeadPriority(id: string, priority: LeadPriority) {
    const items = []

    switch (priority) {
      case LeadPriority.High:
        this.props.setLeadPriority(id, 'Low')
        break
      case LeadPriority.Medium:
        this.props.setLeadPriority(id, 'High')
        break
      default:
        this.props.setLeadPriority(id, 'Medium')
        break
    }
  }

  private _loadNewFeedItems() {
    if (!this.props.isLoadingNew) {
      this.props.loadNewLeads()
    }
  }

  private _noRowsRenderer() {
    return <div>No leads</div>
  }

  private _onItemLayoutChanged(index?: number) {
    if (index === undefined) {
      this.cache.clearAll()
      if (this.tableRef.current) {
        this.tableRef.current.recomputeRowHeights()
      }
    } else {
      this.cache.clear(index, 0)
      if (this.tableRef.current) {
        this.tableRef.current.recomputeRowHeights(index)
      }
    }
  }

  private _onRowClicked(item: IPropertyLead, index: number) {
    window.open(
      `${window.location.pathname.replace('brokerage/leads', 'feed')}/${
        item.lead.id
      }`,
    )
  }

  private _onChangeDueDate(newDueDate: string) {
    this.props.setDueDate(newDueDate)
    this._reloadData()
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeIncludeArchived(value: boolean) {
    this.props.setIncludeArchived(value)
    this._reloadData()
  }

  private _onChangePriority(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setPriority(event.target.value)
    this._reloadData()
  }

  private _onChangePropertyTypes(value: List<PropertyType>) {
    this.props.setPropertyTypes(value)
    this._reloadData()
  }

  private _onChangeScope(newScope: LeadsScope) {
    this.props.setScope(newScope)
    this._reloadData()
  }

  private _onChangeSort(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _onChangeOrderBy(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setOrderBy(event.target.value)
    this._reloadData()
  }

  private _onChangeStatus(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setStatus(event.target.value)
    this._reloadData()
  }

  private _onShowNewItems() {
    this.props.showNewLeads()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }

  private _renderNewItemsNotification() {
    if (this.props.newItems.size === 0) {
      return null
    }

    return (
      <div
        className='text-center cursor-pointer mx-2'
        onClick={this._onShowNewItems}
      >
        <Alert className='alert-hover' color='info'>
          See {this.props.newItems.size} new{' '}
          {this.props.newItems.size > 1 ? 'Leads' : 'Lead'}
        </Alert>
      </div>
    )
  }

  private _toggleShowQuickAdd() {
    this.setState({ showQuickAdd: !this.state.showQuickAdd })
  }
}
